import React, { useRef, useEffect, useState, useCallback } from "react";
import { motion, useAnimation } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import {
  WheelPaletteId,
  getWheelSliceFill,
  getWheelSliceTextColor,
} from "../constants/wheelPalettes";
import {
  SPIN_DURATION_SECONDS,
  playWheelSpinSound,
} from "../utils/wheelSounds";

const MAX_CANVAS_SIZE = 500;
const MIN_CANVAS_SIZE = 200;

/**
 * Props for the NameWheel component
 */
interface NameWheelProps {
  /** Array of names to display on the wheel */
  names: string[];
  /** Controls whether the wheel is currently spinning */
  isSpinning: boolean;
  /** Data for controlling the spin animation */
  spinData?: {
    targetDegrees: number;
    spinRevolutions: number;
  };
  /** Callback function triggered when a name is selected */
  onNameSelected: (name: string) => void;
  /** Callback function to stop the wheel spinning */
  stopSpinning: () => void;
  /** Color palette used for wheel slices */
  paletteId?: WheelPaletteId;
  /** Custom slice colors when the custom palette is selected */
  customColors?: string[];
  /** Whether cheesy wheel sounds should play during a spin */
  soundEnabled?: boolean;
  /** Opens the add-name panel when the wheel is clicked */
  onAddNameClick?: () => void;
}

/**
 * Component that renders an interactive spinning wheel with names.
 * Features smooth animations, customizable appearance, and random selection.
 * Supports empty state and dynamic resizing.
 *
 * @param props - The component props
 * @returns An animated wheel component for name selection
 */
const NameWheel: React.FC<NameWheelProps> = ({
  names,
  isSpinning,
  spinData,
  onNameSelected,
  stopSpinning,
  paletteId = "rainbow",
  customColors = [],
  soundEnabled = true,
  onAddNameClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const controls = useAnimation();
  const [canvasSize, setCanvasSize] = useState(MAX_CANVAS_SIZE);
  const [lastLandedDegrees, setLastLandedDegrees] = useState<number>(0);
  const { theme } = useTheme();
  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;
  const namesSignature = names.join("\u0001");

  const radius = canvasSize / 2 - canvasSize * 0.06;
  const labelFontSize = Math.max(12, Math.round(canvasSize * 0.032));
  const emptyFontSize = Math.max(14, Math.round(canvasSize * 0.04));
  const pointerWidth = Math.max(12, Math.round(canvasSize * 0.04));
  const pointerHeight = Math.max(24, Math.round(canvasSize * 0.08));

  const updateCanvasSize = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth;
    if (width <= 0) return;

    const size = Math.min(
      MAX_CANVAS_SIZE,
      Math.max(MIN_CANVAS_SIZE, Math.floor(width)),
    );
    setCanvasSize(size);
  }, []);

  useEffect(() => {
    updateCanvasSize();
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(updateCanvasSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, [updateCanvasSize]);

  useEffect(() => {
    if (isSpinning) {
      return;
    }
    controls.set({ rotate: 0 });
    setLastLandedDegrees(0);
  }, [namesSignature, controls, isSpinning]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasSize * dpr;
    canvas.height = canvasSize * dpr;
    canvas.style.width = `${canvasSize}px`;
    canvas.style.height = `${canvasSize}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const drawWheel = () => {
      ctx.clearRect(0, 0, canvasSize, canvasSize);

      ctx.beginPath();
      ctx.arc(canvasSize / 2, canvasSize / 2, radius, 0, 2 * Math.PI);
      ctx.fillStyle = theme.backgroundColor;
      ctx.fill();
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 2;
      ctx.stroke();

      if (names.length === 0) {
        ctx.beginPath();
        ctx.moveTo(canvasSize / 2, canvasSize / 2);
        ctx.arc(canvasSize / 2, canvasSize / 2, radius, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.fillStyle = "#b0f2ff";
        ctx.fill();
        ctx.stroke();

        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#000000";
        ctx.font = `${emptyFontSize}px Arial`;
        ctx.fillText("Click to add names", canvasSize / 2, canvasSize / 2);
        ctx.restore();
        return;
      }

      const segmentAngle = 360 / names.length;
      const startOffset = -90 * (Math.PI / 180);

      names.forEach((name, index) => {
        const startAngle = startOffset + (index * segmentAngle * Math.PI) / 180;
        const endAngle =
          startOffset + ((index + 1) * segmentAngle * Math.PI) / 180;

        ctx.beginPath();
        ctx.moveTo(canvasSize / 2, canvasSize / 2);
        ctx.arc(canvasSize / 2, canvasSize / 2, radius, startAngle, endAngle);
        ctx.closePath();

        const fill = getWheelSliceFill(
          index,
          names.length,
          paletteId,
          customColors,
        );
        ctx.fillStyle = fill;
        ctx.fill();
        ctx.stroke();

        ctx.save();
        ctx.translate(canvasSize / 2, canvasSize / 2);
        const textAngle = startAngle + (segmentAngle * Math.PI) / 360;
        ctx.rotate(textAngle);
        ctx.textAlign = "right";
        ctx.fillStyle = getWheelSliceTextColor(fill);
        ctx.font = `${labelFontSize}px Arial`;
        ctx.fillText(name, radius - 10, 0);
        ctx.restore();
      });
    };

    drawWheel();
  }, [
    names,
    radius,
    theme.backgroundColor,
    paletteId,
    customColors,
    canvasSize,
    labelFontSize,
    emptyFontSize,
  ]);

  useEffect(() => {
    if (!isSpinning || !spinData || names.length === 0) {
      return;
    }

    const { targetDegrees, spinRevolutions } = spinData;
    const totalRotation = spinRevolutions * 360 + targetDegrees;

    controls.start({
      rotate: [lastLandedDegrees, totalRotation],
      transition: {
        duration: SPIN_DURATION_SECONDS,
        ease: [0.32, 0, 0.24, 1],
      },
    });

    const stopSound = soundEnabledRef.current
      ? playWheelSpinSound({
          duration: SPIN_DURATION_SECONDS,
          sliceCount: names.length,
          totalRotation: Math.max(360, totalRotation - lastLandedDegrees),
        })
      : undefined;

    let spinFinished = false;
    const timeoutId = window.setTimeout(() => {
      spinFinished = true;
      const degreesPerSlice = 360 / names.length;
      const adjustedDegrees = totalRotation % 360;
      const normalizedDegrees = (360 - (adjustedDegrees % 360)) % 360;
      const selectedIndex = Math.floor(normalizedDegrees / degreesPerSlice);
      const selectedName = names[selectedIndex];

      setLastLandedDegrees(totalRotation % 360);
      onNameSelected(selectedName);
      stopSpinning();
    }, SPIN_DURATION_SECONDS * 1000);

    return () => {
      window.clearTimeout(timeoutId);
      if (!spinFinished) {
        stopSound?.();
      }
    };
  }, [
    isSpinning,
    spinData,
    controls,
    names,
    onNameSelected,
    stopSpinning,
    lastLandedDegrees,
  ]);

  return (
    <div
      ref={containerRef}
      className="aspect-square w-full max-w-[500px]"
    >
      <div
        className={`relative mx-auto ${isSpinning ? "" : "cursor-pointer"}`}
        style={{ width: canvasSize, height: canvasSize }}
        onClick={() => {
          if (!isSpinning) {
            onAddNameClick?.();
          }
        }}
        title={isSpinning ? undefined : "Click to add a name"}
      >
        <div
          className="absolute left-1/2 top-0 z-10 -translate-x-1/2"
          style={{
            width: 0,
            height: 0,
            borderLeft: `${pointerWidth}px solid transparent`,
            borderRight: `${pointerWidth}px solid transparent`,
            borderTop: `${pointerHeight}px solid black`,
          }}
        />
        <motion.div animate={controls} className="inline-block">
          <canvas ref={canvasRef} />
        </motion.div>
      </div>
    </div>
  );
};

export default NameWheel;
